import { useMemo } from 'react';
import bwipjs from '@bwip-js/browser';
import { getBarcodeDefinition, resolveBarcodeSymbology, resolveBarcodeValue } from '../lib/barcodes.js';
import { resolveFont } from '../studio/zebra/ZebraFonts.js';
import { dotsToMm } from '../studio/zebra/ZebraUnits.js';
import ProtectedImage from './ProtectedImage.jsx';

function num(value, fallback = 0) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

// Tamanho físico do texto (mm): dots (modelo migrado) → mm; bitmap → matriz; escalável → pt→mm.
function textFontSizeMm(obj, dpi) {
  const font = obj.font;
  if (font && typeof font === 'object' && typeof font.height === 'number' && font.height > 0) {
    return dotsToMm(font.height, dpi);
  }
  const profile = resolveFont(font);
  if (!profile.scalable) {
    return dotsToMm(profile.matrix?.h ?? 0, dpi);
  }
  return (num(obj.fontSize, 12) * 25.4) / 72;
}

function isCenter(align) {
  return align === 'center' || align === 'C';
}

function isRight(align) {
  return align === 'right' || align === 'R';
}

const QR_BYTE_CAPACITY_ECC_M = [
  14, 26, 42, 62, 84, 106, 122, 152, 180, 213, 251, 287, 331, 362, 412, 450,
  504, 560, 624, 666, 711, 779, 857, 911, 997, 1059, 1125, 1190, 1264, 1370,
  1452, 1538, 1628, 1722, 1809, 1911, 1989, 2099, 2213, 2331,
];

function resolveBarcodeModuleWidth(obj, value, widthDots) {
  const configured = num(obj.moduleWidth, 0);
  if (configured > 0) return configured;
  if (widthDots <= 0) return 2;
  const data = String(value ?? '');
  const symbology = resolveBarcodeSymbology(obj);
  const modules = symbology === 'ean13' ? 95 : 11 * Math.max(1, data.length) + 35;
  return Math.max(1, Math.floor(widthDots / modules));
}

function resolveQrMagnification(obj, value, widthDots) {
  const configured = num(obj.module, 0);
  if (configured > 0) return configured;
  if (widthDots <= 0) return 4;
  const length = new TextEncoder().encode(String(value ?? '')).length;
  let version = 1;
  while (version <= QR_BYTE_CAPACITY_ECC_M.length && length > QR_BYTE_CAPACITY_ECC_M[version - 1]) {
    version += 1;
  }
  const modules = 21 + 4 * (version - 1) + 8;
  return Math.max(1, Math.floor(widthDots / modules));
}

function BarcodeSymbol({ obj, value, dpi = 203 }) {
  const definition = getBarcodeDefinition(resolveBarcodeSymbology(obj));
  const encodedValue = String(value || definition.defaultValue);
  
  const widthDots = Math.round(((obj.w || definition.defaultWidth || 30) * dpi) / 25.4);
  const is2D = definition.dimension === '2D';

  const svgMarkup = useMemo(() => {
    try {
      if (is2D) {
        const mag = resolveQrMagnification(obj, encodedValue, widthDots);
        const svg = bwipjs.toSVG({
          bcid: definition.bcid,
          text: encodedValue,
          scale: Math.max(1, mag),
          includetext: false,
        });
        return svg.replace('<svg ', '<svg width="100%" height="100%" preserveAspectRatio="xMinYMin meet" ');
      }

      const modWidth = resolveBarcodeModuleWidth(obj, encodedValue, widthDots);
      const barcodeHeightDots = Math.max(16, Math.round(((obj.h || definition.defaultHeight || 15) - (obj.showText ? 4 : 0)) * dpi / 25.4));

      const svg = bwipjs.toSVG({
        bcid: definition.bcid,
        text: encodedValue,
        scaleX: Math.max(1, modWidth),
        scaleY: 1,
        height: Math.round(dotsToMm(barcodeHeightDots, dpi)),
        includetext: false,
      });

      return svg.replace('<svg ', '<svg width="100%" height="100%" preserveAspectRatio="xMinYMid meet" ');
    } catch (error) {
      return null;
    }
  }, [definition, obj, encodedValue, widthDots, is2D, dpi]);

  if (!svgMarkup) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center rounded border border-dashed border-error/30 px-2 text-center text-[8px] text-error">
        <strong>{definition.label}</strong>
        <span>Valor inválido para esta simbologia</span>
      </div>
    );
  }

  return <div className="h-full w-full overflow-hidden" dangerouslySetInnerHTML={{ __html: svgMarkup }} />;
}

function LabelObject({ obj, values, dpi }) {
  const style = {
    position: 'absolute',
    left: `${obj.x}mm`,
    top: `${obj.y}mm`,
    width: `${obj.w}mm`,
    height: `${obj.h}mm`,
  };

  switch (obj.type) {
    case 'text':
    case 'variable': {
      const profile = resolveFont(obj.font);
      const rawValue = obj.type === 'variable' ? values[obj.field] || obj.placeholder || '' : obj.text || '';
      const lines = String(rawValue ?? '').split(/\r?\n/);
      const letterSpacing = profile.scalable ? undefined : `${dotsToMm(profile.icg ?? 0, dpi)}mm`;
      const fontHeightMm = textFontSizeMm(obj, dpi);
      const lineHeightMm = fontHeightMm * 1.14;

      return (
        <div
          style={{
            ...style,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: isCenter(obj.align) ? 'center' : 'flex-start',
            overflow: 'hidden',
          }}
        >
          {lines.map((lineText, idx) => (
            <div
              key={idx}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: isCenter(obj.align) ? 'center' : isRight(obj.align) ? 'flex-end' : 'flex-start',
                fontFamily: profile.previewFamily,
                fontSize: `${fontHeightMm}mm`,
                fontWeight: profile.scalable && obj.bold ? 700 : 400,
                letterSpacing,
                color: obj.color || '#111111',
                lineHeight: `${lineHeightMm}mm`,
                minHeight: `${lineHeightMm}mm`,
                whiteSpace: 'nowrap',
              }}
            >
              {lineText}
            </div>
          ))}
        </div>
      );
    }
    case 'barcode':
    case 'qrcode': {
      const definition = getBarcodeDefinition(resolveBarcodeSymbology(obj));
      const encodedValue = resolveBarcodeValue(obj, values);
      return (
        <div style={{ ...style, display: 'flex', flexDirection: 'column' }}>
          <div className="min-h-0 flex-1">
            <BarcodeSymbol obj={obj} value={encodedValue} dpi={dpi} />
          </div>
          {definition.supportsText && obj.showText && (
            <span className="font-mono text-center tracking-widest" style={{ fontSize: `${obj.textSize || 10}px` }}>
              {encodedValue}
            </span>
          )}
        </div>
      );
    }
    case 'shape':
    case 'rect':
      return (
        <div
          style={{
            ...style,
            background: obj.fill || 'transparent',
            border: `${obj.strokeWidth || 1}px solid ${obj.stroke || obj.fill || '#000'}`,
            borderRadius: `${obj.radius || 0}mm`,
            boxSizing: 'border-box',
          }}
        />
      );
    case 'line':
      return (
        <svg style={style} viewBox={`0 0 ${Math.max(1, obj.w)} ${Math.max(1, obj.h)}`} preserveAspectRatio="none">
          <line
            x1="0"
            y1="0"
            x2={Math.max(1, obj.w)}
            y2={Math.max(1, obj.h)}
            stroke={obj.stroke || '#000'}
            strokeWidth={obj.strokeWidth || 1}
          />
        </svg>
      );
    case 'ellipse':
      return (
        <svg style={style} viewBox={`0 0 ${Math.max(1, obj.w)} ${Math.max(1, obj.h)}`} preserveAspectRatio="none">
          <ellipse
            cx={Math.max(1, obj.w) / 2}
            cy={Math.max(1, obj.h) / 2}
            rx={Math.max(1, obj.w) / 2 - (obj.strokeWidth || 1) / 2}
            ry={Math.max(1, obj.h) / 2 - (obj.strokeWidth || 1) / 2}
            fill={obj.fill || 'transparent'}
            stroke={obj.stroke || '#000'}
            strokeWidth={obj.strokeWidth || 1}
          />
        </svg>
      );
    case 'image':
      return obj.src ? (
        <ProtectedImage
          src={obj.src}
          alt=""
          style={{
            ...style,
            objectFit: obj.fit || 'contain',
            borderRadius: `${obj.borderRadius || 0}mm`,
          }}
          fallback={
            <div
              style={{
                ...style,
                border: '1px dashed #94a3b8',
                background: '#f8fafc',
                borderRadius: `${obj.borderRadius || 0}mm`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                fontSize: '8px',
                textAlign: 'center',
                padding: '2px',
                boxSizing: 'border-box',
              }}
            >
              imagem
            </div>
          }
        />
      ) : (
        <div
          style={{
            ...style,
            border: '1px dashed #94a3b8',
            background: '#f8fafc',
            borderRadius: `${obj.borderRadius || 0}mm`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#64748b',
            fontSize: '8px',
            textAlign: 'center',
            padding: '2px',
            boxSizing: 'border-box',
          }}
        >
          imagem
        </div>
      );
    default:
      return null;
  }
}

export default function LabelPreview({ content, values = {}, scale = 1, columns = 1, gapMm = 0, dpi = 203 }) {
  const { width = 102, height = 152, objects = [] } = content || {};
  const count = Math.max(1, Number(columns) || 1);
  return (
    <div
      style={{
        display: 'flex',
        gap: `${gapMm}mm`,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
      }}
    >
      {Array.from({ length: count }).map((_, index) => (
        <div
          key={index}
          style={{
            width: `${width}mm`,
            height: `${height}mm`,
            position: 'relative',
            background: '#fff',
            flexShrink: 0,
          }}
        >
          {objects.map((obj) => (
            <LabelObject key={obj.id} obj={obj} values={values} dpi={dpi} />
          ))}
        </div>
      ))}
    </div>
  );
}
